import {
	type CollectionFieldLinePoint,
	type CollectionFieldLineValue,
	type ContinuousSettings,
	IkeFieldType,
	Public,
} from '@ikegps/core-api';
import { IkeGeodesic } from '@ikegps/core-measurement';
import type { FormModelFieldLine } from '../../form/form.line';
import { CollectionModelField, ModelMutator } from '../collection.field';
import { registerFieldType } from './field.registry';

export type ModelLinePoint = CollectionFieldLinePoint;

export interface ModelFieldStateLineOffice {
	provider: 'office';
	value: CollectionFieldLineValue;
}

export interface ModelFieldStateLineFieldManual {
	provider: 'field_manual';
	/** Device antenna height off the ground (Meters). Field-manual only. */
	antennaHeight: number;
	value: CollectionFieldLineValue;
}

export interface ModelFieldStateLineFieldContinuous {
	provider: 'field_continuous';
	continuousSettings: ContinuousSettings;
	/** Device antenna height off the ground (Meters). Optional here, unlike field-manual. */
	antennaHeight?: number;
	value: CollectionFieldLineValue;
}

export type ModelFieldStateLine =
	| ModelFieldStateLineOffice
	| ModelFieldStateLineFieldManual
	| ModelFieldStateLineFieldContinuous;

export class ModelMutatorLine extends ModelMutator<FormModelFieldLine, CollectionModelFieldLine> {
	static officePoint(latitude: number, longitude: number): ModelLinePoint {
		return { latitude, longitude, altitude: null, locationFix: 'manual', note: '', collector: 'office' };
	}

	private resolveLineType(title: string): string {
		return this.field.schema.findLineType(title) != null ? title : '';
	}

	private writeValue(state: ModelFieldStateLine | null) {
		this.state.setFieldValue<ModelFieldStateLine>(this.field.id, state);
	}

	drawOffice(points: ModelLinePoint[]) {
		this.writeValue({
			provider: 'office',
			value: { lineType: this.resolveLineType(this.field.lineType), points: points.slice() },
		});
	}

	setLineType(title: string) {
		const current = this.field.stateValue;
		const lineType = this.resolveLineType(title);
		if (current == null) {
			this.writeValue({ provider: 'office', value: { lineType, points: [] } });
			return;
		}
		this.writeValue({ ...current, value: { ...current.value, lineType } });
	}

	addPoint(point: ModelLinePoint) {
		const current = this.field.stateValue;
		if (current == null) {
			this.writeValue({ provider: 'office', value: { lineType: '', points: [point] } });
			return;
		}
		this.writeValue({ ...current, value: { ...current.value, points: [...current.value.points, point] } });
	}

	deleteVertex(index: number) {
		this.updatePoints(index, (points) => {
			points.splice(index, 1);
			return points;
		});
	}

	setAltitude(index: number, altitude: number | null) {
		this.updatePoint(index, (point) => ({ ...point, altitude }));
	}

	setNote(index: number, note: string) {
		this.updatePoint(index, (point) => ({ ...point, note }));
	}

	clearNote(index: number) {
		this.setNote(index, '');
	}

	office(value: CollectionFieldLineValue) {
		this.writeValue({ provider: 'office', value: this.readValue(value) });
	}

	fieldManual(value: CollectionFieldLineValue, antennaHeight: number) {
		this.writeValue({ provider: 'field_manual', antennaHeight, value: this.readValue(value) });
	}

	/**
	 * Single choke point every read path funnels through, so `0` is normalized to absent here
	 * rather than at each caller.
	 */
	fieldContinuous(value: CollectionFieldLineValue, continuousSettings: ContinuousSettings, antennaHeight?: number) {
		this.writeValue({
			provider: 'field_continuous',
			continuousSettings,
			antennaHeight: antennaHeight != null && antennaHeight > 0 ? antennaHeight : undefined,
			value: this.readValue(value),
		});
	}

	override copy(from: CollectionModelFieldLine): boolean {
		const state = from.stateValue;
		if (state == null) {
			this.clear();
			return true;
		}
		const cloned: ModelFieldStateLine = JSON.parse(JSON.stringify(state));
		const storedType = cloned.value.lineType;
		cloned.value.lineType = this.resolveLineType(storedType);

		if (storedType !== '' && cloned.value.lineType === '') {
			this.clear();
			return true;
		}
		this.writeValue(cloned);
		return true;
	}

	private readValue(value: CollectionFieldLineValue | null | undefined): CollectionFieldLineValue {
		const storedType = value?.lineType ?? '';
		const lineType = this.resolveLineType(storedType);

		if (storedType !== '' && lineType === '') {
			return { lineType: '', points: [] };
		}
		return { lineType, points: Array.isArray(value?.points) ? value.points : [] };
	}

	private updatePoint(index: number, change: (point: ModelLinePoint) => ModelLinePoint) {
		this.updatePoints(index, (points) => {
			points[index] = change(points[index]);
			return points;
		});
	}

	private updatePoints(index: number, change: (points: ModelLinePoint[]) => ModelLinePoint[]) {
		const current = this.field.stateValue;
		if (current == null || current.value.points[index] == null) {
			return;
		}
		const points = change(current.value.points.slice());
		this.writeValue({ ...current, value: { ...current.value, points } });
	}
}

export class CollectionModelFieldLine extends CollectionModelField<
	FormModelFieldLine,
	ModelFieldStateLine,
	CollectionFieldLineValue
> {
	static readonly PROVIDER_OFFICE = 'office' as const;
	static readonly PROVIDER_FIELD_MANUAL = 'field_manual' as const;
	static readonly PROVIDER_FIELD_CONTINUOUS = 'field_continuous' as const;

	static TYPE: IkeFieldType = IkeFieldType.LINE;

	static isLineField(f: CollectionModelField): f is CollectionModelFieldLine {
		return f.schema.type === CollectionModelFieldLine.TYPE;
	}

	private _Mutator: ModelMutatorLine;
	get Mutator() {
		if (this._Mutator == null) {
			this._Mutator = new ModelMutatorLine(this);
		}
		return this._Mutator;
	}

	get lineType(): string {
		const stored = this.stateValue?.value?.lineType ?? '';
		return this.schema.findLineType(stored) != null ? stored : '';
	}

	get points(): ModelLinePoint[] {
		return this.stateValue?.value?.points ?? [];
	}

	get pointCount(): number {
		return this.points.length;
	}

	private _distanceMeters = 0;
	private _distanceSource: ModelLinePoint[] | null = null;

	/**
	 * Total geodesic length of the polyline (meters). Cached against the points array identity
	 * (mutations always write a new array) so the geodesic sum recomputes only on change.
	 */
	get totalDistanceMeters(): number {
		const points = this.points;
		if (this._distanceSource !== points) {
			this._distanceMeters = IkeGeodesic.getPolylineDistance(points);
			this._distanceSource = points;
		}
		return this._distanceMeters;
	}

	override get value(): CollectionFieldLineValue {
		if (this.stateValue == null) {
			return null;
		}
		return { lineType: this.lineType, points: this.points };
	}

	/**
	 * The selected line-type title, or '' when it no longer resolves against the schema. Mirrors the
	 * select-list contract so Form Upgrade's displayValue comparison flags a removed line type as `replace`.
	 */
	override get displayValue(): string {
		return this.lineType;
	}

	get provider(): 'office' | 'field_manual' | 'field_continuous' {
		return this.stateValue?.provider ?? CollectionModelFieldLine.PROVIDER_OFFICE;
	}

	get antennaHeight(): number | null {
		const state = this.stateValue;
		if (state == null) {
			return null;
		}
		if (state.provider === 'field_manual') {
			return state.antennaHeight;
		}
		if (state.provider === 'field_continuous') {
			return state.antennaHeight ?? null;
		}
		return null;
	}

	get continuousSettings(): ContinuousSettings | null {
		const state = this.stateValue;
		return state != null && state.provider === 'field_continuous' ? state.continuousSettings : null;
	}

	isOffice(): this is CollectionModelField<FormModelFieldLine, ModelFieldStateLineOffice, CollectionFieldLineValue> {
		return this.provider === CollectionModelFieldLine.PROVIDER_OFFICE;
	}

	isFieldManual(): this is CollectionModelField<
		FormModelFieldLine,
		ModelFieldStateLineFieldManual,
		CollectionFieldLineValue
	> {
		return this.provider === CollectionModelFieldLine.PROVIDER_FIELD_MANUAL;
	}

	isFieldContinuous(): this is CollectionModelField<
		FormModelFieldLine,
		ModelFieldStateLineFieldContinuous,
		CollectionFieldLineValue
	> {
		return this.provider === CollectionModelFieldLine.PROVIDER_FIELD_CONTINUOUS;
	}

	hasValue(): boolean {
		return this.pointCount >= 2;
	}

	/**
	 * Legal-state invariant: a line is either fully empty or fully specified (a type AND ≥2 points),
	 * never half-specified. The value-side counterpart to the schema's FormModelFieldLine.isValid.
	 */
	isComplete(): boolean {
		return (this.lineType !== '') === this.hasValue();
	}

	/**
	 * Single source for which provider-exclusive attribute (antennaHeight / continuousSettings) belongs to
	 * which provider; both serialization layers derive their shape from this rather than re-reading raw state.
	 */
	providerDescriptor(): Public.CollectionFieldLineProvider {
		const state = this.stateValue;
		if (state?.provider === 'field_manual') {
			return { type: 'field_manual', antennaHeight: state.antennaHeight };
		}
		if (state?.provider === 'field_continuous') {
			return {
				type: 'field_continuous',
				continuousSettings: state.continuousSettings,
				...(state.antennaHeight != null ? { antennaHeight: state.antennaHeight } : {}),
			};
		}
		return { type: 'office' };
	}
}

registerFieldType(
	IkeFieldType.LINE,
	(parent, schema) => new CollectionModelFieldLine(parent, <FormModelFieldLine>schema)
);
